/**
 * Modal per visualizzare dettagli pianificatore e impianti assegnati
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Avatar,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  PhoneAndroid as MobileIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  LocalGasStation as PlantIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { plannersApi } from '../../services/api';

const InfoRow = ({ icon, label, value, chips }) => {
  if (!value && !chips?.length) return null;
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
      <Box sx={{ color: 'text.secondary', mt: 0.5 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        {chips ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            {chips.map((c, i) => (
              <Chip key={i} label={c} size="small" variant="outlined" />
            ))}
          </Box>
        ) : (
          <Typography variant="body2">{value}</Typography>
        )}
      </Box>
    </Box>
  );
};

const ViewPlannerModal = ({ open, onClose, planner, supplierName, onAssignPlants, onEdit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Carica il dettaglio completo del pianificatore (include gli impianti assegnati)
  const plannerDetailQuery = useQuery({
    queryKey: ['planner', planner?.id],
    queryFn: () => plannersApi.getById(planner?.id),
    enabled: open && !!planner?.id
  });

  // Usa i dati dettagliati se disponibili, altrimenti i dati passati
  const plannerData = plannerDetailQuery.data?.data?.data || planner;
  
  if (!planner) return null;

  // Parse coverage arrays
  let provinces = plannerData?.coverage_provinces || [];
  let cities = plannerData?.coverage_cities || [];
  
  if (typeof provinces === 'string') {
    try { provinces = JSON.parse(provinces); } catch (e) { provinces = []; }
  }
  if (typeof cities === 'string') {
    try { cities = JSON.parse(cities); } catch (e) { cities = []; }
  }

  const assignedPlants = plannerData?.assignedPlants || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: plannerData?.is_active ? 'primary.main' : 'grey.400' }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6">{plannerData?.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {plannerData?.role || 'Pianificatore'}
              {plannerData?.is_primary && (
                <Chip 
                  icon={<StarIcon />} 
                  label="Primario" 
                  size="small" 
                  color="warning"
                  sx={{ ml: 1 }}
                />
              )}
              {plannerData?.emergency_contact && (
                <Chip 
                  icon={<WarningIcon />} 
                  label="Emergenze" 
                  size="small" 
                  color="error"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
          </Box>
          {!plannerData?.is_active && (
            <Chip label="Non attivo" color="default" size="small" />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {plannerDetailQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
        <Grid container spacing={3}>
          {/* Info di contatto */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Informazioni di contatto
            </Typography>
            
            <InfoRow 
              icon={<BusinessIcon />} 
              label="Fornitore" 
              value={supplierName || plannerData?.supplier?.company_name || 'N/D'} 
            />
            <InfoRow 
              icon={<EmailIcon />} 
              label="Email" 
              value={plannerData?.email} 
            />
            <InfoRow 
              icon={<PhoneIcon />} 
              label="Telefono" 
              value={plannerData?.phone} 
            />
            <InfoRow 
              icon={<MobileIcon />} 
              label="Cellulare" 
              value={plannerData?.mobile} 
            />
            <InfoRow 
              icon={<TimeIcon />} 
              label="Orari di lavoro" 
              value={plannerData?.working_hours} 
            />
          </Grid>

          {/* Copertura geografica */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Copertura geografica
            </Typography>
            
            <InfoRow 
              icon={<LocationIcon />} 
              label="Regione" 
              value={plannerData?.coverage_region || 'Tutte'} 
            />
            
            {provinces.length > 0 && (
              <InfoRow 
                icon={<LocationIcon />} 
                label="Province" 
                chips={provinces} 
              />
            )}
            
            {cities.length > 0 && (
              <InfoRow 
                icon={<LocationIcon />} 
                label="Città" 
                chips={cities} 
              />
            )}
          </Grid>

          {/* Note */}
          {plannerData?.notes && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Note
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {plannerData.notes}
              </Typography>
            </Grid>
          )}

          {/* Impianti assegnati */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                <PlantIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Impianti assegnati ({assignedPlants.length})
              </Typography>
              {onAssignPlants && (
                <Button
                  size="small"
                  startIcon={<AssignmentIcon />}
                  onClick={() => onAssignPlants(plannerData)}
                  variant="outlined"
                >
                  Gestisci Impianti
                </Button>
              )}
            </Box>
            
            {assignedPlants.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Nessun impianto assegnato a questo pianificatore
              </Typography>
            ) : (
              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                  {assignedPlants.map((plant) => (
                    <ListItem key={plant.id} divider>
                      <ListItemIcon>
                        <PlantIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {plant.plant_code || plant.delivery_code || plant.id}
                            </Typography>
                            <Typography variant="body2">
                              {plant.nome || '-'}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <span>
                            {[plant.citta, plant.provincia].filter(Boolean).join(' ')}
                            {plant.indirizzo && ` - ${plant.indirizzo}`}
                          </span>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Grid>
        </Grid>
        )}
      </DialogContent>
      
      <DialogActions>
        {onEdit && (
          <Button 
            startIcon={<EditIcon />} 
            onClick={() => onEdit(plannerData)}
          >
            Modifica
          </Button>
        )}
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewPlannerModal;
